import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, BellOff, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    isPushSupported, notificationPermission, subscribeToPush,
    unsubscribeFromPush, sendPushTest, currentSubscription,
} from "@/lib/push";

/**
 * Notifications settings card — lets the user opt in/out of PWA push and
 * fire a test notification. Rendered on /app/settings.
 */
export default function NotificationSettingsCard() {
    const supported = isPushSupported();
    const [subscribed, setSubscribed] = useState(false);
    const [permission, setPermission] = useState(supported ? notificationPermission() : "unsupported");
    const [busy, setBusy] = useState(false);
    const { user, refresh } = useAuth();
    const [nudgeOptOut, setNudgeOptOut] = useState(!!user?.profile_nudge_opt_out);

    useEffect(() => {
        setNudgeOptOut(!!user?.profile_nudge_opt_out);
    }, [user?.profile_nudge_opt_out]);

    useEffect(() => {
        if (!supported) return;
        (async () => {
            setSubscribed(!!(await currentSubscription()));
            setPermission(notificationPermission());
        })();
    }, [supported]);

    async function toggle(want) {
        setBusy(true);
        try {
            if (want) {
                await subscribeToPush();
                setSubscribed(true);
                setPermission("granted");
                toast.success("Push notifications enabled — you'll get live trade + drawdown alerts.");
            } else {
                await unsubscribeFromPush();
                setSubscribed(false);
                toast.success("Push notifications turned off. Turn them back on anytime.");
            }
        } catch (e) {
            const msg = e?.message || "Couldn't update push notification settings.";
            toast.error(msg);
            if (msg.includes("permission denied")) setPermission("denied");
        } finally { setBusy(false); }
    }

    async function toggleNudge(next) {
        // next === true means user wants nudges (toggle ON). Backend stores opt_OUT.
        setBusy(true);
        try {
            await api.post("/profile/nudge-opt-out", { opt_out: !next });
            setNudgeOptOut(!next);
            toast.success(next
                ? "You'll get a one-time nudge if you leave your profile half-set-up."
                : "Profile-completion nudges turned off.");
            if (refresh) await refresh();
        } catch {
            toast.error("Couldn't update nudge preferences.");
        } finally { setBusy(false); }
    }

    async function testFire() {
        setBusy(true);
        try {
            await sendPushTest();
            toast.success("Test notification sent — check your system tray in a few seconds.");
        } catch {
            toast.error("Couldn't send test notification.");
        } finally { setBusy(false); }
    }

    return (
        <div className="border border-border bg-card rounded p-4" data-testid="notif-settings-card">
            <div className="overline mb-3 flex items-center gap-2">
                <Bell className="w-3.5 h-3.5" /> Push notifications
            </div>

            {!supported && (
                <div className="text-sm text-muted-foreground">
                    Push notifications aren't supported by this browser. Try Chrome, Firefox, Edge, or Safari 16.4+ on iOS
                    (with the PWA installed to your home screen).
                </div>
            )}

            {supported && (
                <>
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm">
                            <div className="font-semibold">Trade + drawdown alerts</div>
                            <div className="text-xs text-muted-foreground">
                                Get pinged when your copy leader takes a trade, or when a drawdown breach hits.
                            </div>
                        </div>
                        <Switch
                            checked={subscribed}
                            onCheckedChange={toggle}
                            disabled={busy || permission === "denied"}
                            data-testid="push-toggle"
                        />
                    </div>

                    {permission === "denied" && (
                        <div className="text-xs text-danger mb-3 flex items-start gap-1.5" data-testid="push-denied">
                            <BellOff className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span>
                                Notifications are blocked at the browser level. Open your browser's site settings for
                                this page and set Notifications to Allow, then flip the switch again.
                            </span>
                        </div>
                    )}

                    {subscribed && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={testFire}
                            disabled={busy}
                            data-testid="push-test"
                            className="gap-1.5"
                        >
                            <Send className="w-3.5 h-3.5" /> Send test notification
                        </Button>
                    )}
                </>
            )}

            {/* Profile completion nudge — independent of push support (in-app notif works everywhere) */}
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                <div className="text-sm">
                    <div className="font-semibold">Profile completion reminder</div>
                    <div className="text-xs text-muted-foreground">
                        A one-time push + in-app nudge if you leave your profile 50–99% set up for 48 hours.
                    </div>
                </div>
                <Switch
                    checked={!nudgeOptOut}
                    onCheckedChange={toggleNudge}
                    disabled={busy}
                    data-testid="profile-nudge-toggle"
                />
            </div>
        </div>
    );
}
